/**
 * Zod validation schemas for Warehouse types
 */

import { z } from 'zod';
import type {
  WarehouseResponse,
  CreateWarehouseRequest,
  UpdateWarehouseRequest,
} from '@kisanlink/erp-service';

// Address validation schema for warehouse forms
export const warehouseAddressSchema = z.object({
  type: z.enum(['HOME', 'WORK', 'OTHER']).default('WORK'),
  house: z.string().optional(),
  street: z.string().min(1, 'Street is required'),
  landmark: z.string().optional(),
  post_office: z.string().optional(),
  subdistrict: z.string().optional(),
  district: z.string().optional(),
  vtc: z.string().min(1, 'City/Town is required'),
  state: z.string().min(1, 'State is required'),
  country: z.string().default('India'),
  pincode: z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits'),
  is_primary: z.boolean().default(true),
});

export const warehouseSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Warehouse name is required'),
  address: z.any().optional(), // AddressInfo type
  created_at: z.string(),
  updated_at: z.string(),
}) satisfies z.ZodType<WarehouseResponse>;

export const createWarehouseSchema = z.object({
  name: z.string().min(1, 'Warehouse name is required'),
  address_id: z.string().optional(),
  address: z.any().optional(), // CreateAddressRequest type
}) satisfies z.ZodType<CreateWarehouseRequest>;

export const updateWarehouseSchema = z.object({
  name: z.string().min(1, 'Warehouse name is required').optional(),
  address_id: z.string().optional(),
  address: z.any().optional(), // UpdateAddressRequest type
}) satisfies z.ZodType<UpdateWarehouseRequest>;

// Form-specific schema for warehouse creation/editing
export const warehouseFormSchema = z.object({
  name: z.string().min(1, 'Warehouse name is required'),
  description: z.string().optional(),
  address: warehouseAddressSchema,
});

export type Warehouse = z.infer<typeof warehouseSchema>;
export type CreateWarehouse = z.infer<typeof createWarehouseSchema>;
export type UpdateWarehouse = z.infer<typeof updateWarehouseSchema>;
export type WarehouseFormData = z.infer<typeof warehouseFormSchema>;
export type WarehouseAddress = z.infer<typeof warehouseAddressSchema>;
